import { useEffect, useState, useCallback } from "react";
import { apiClient } from "../App";
import { Card } from "./ui/card";
import { Badge } from "./ui/badge";
import { Button } from "./ui/button";
import { Loader2, AlertTriangle, RefreshCw } from "lucide-react";

const SEV = {
  Critical: "bg-rose-100 text-rose-900 border-rose-300",
  High: "bg-rose-50 text-rose-800 border-rose-200",
  Medium: "bg-amber-50 text-amber-800 border-amber-200",
  Low: "bg-slate-50 text-slate-700 border-slate-200",
};

export default function RiskRadar() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await apiClient.get("/risk-radar");
      setData(res.data);
    } catch (e) {
      if (e?.response?.status === 403) setData({ forbidden: true });
      else setData({ error: true });
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  if (loading) return (<Card className="p-4 border-slate-200" data-testid="risk-radar-loading"><div className="flex items-center gap-2 text-slate-500 text-sm"><Loader2 className="h-4 w-4 animate-spin"/>Scanning for risk…</div></Card>);
  if (!data || data.forbidden || data.error) return null;

  const rows = data.managers || [];
  const fmtGBP = (n) => `£${Math.round(n || 0).toLocaleString("en-GB")}`;

  return (
    <Card className="p-4 border-slate-200 bg-white" data-testid="risk-radar">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-rose-600"/>
          <h3 className="font-semibold text-slate-900 text-sm">Risk Radar</h3>
          <span className="text-xs text-slate-500">{rows.length} flag(s)</span>
        </div>
        <Button variant="ghost" size="sm" onClick={load} className="h-7 px-2" data-testid="risk-radar-refresh"><RefreshCw className="h-3.5 w-3.5"/></Button>
      </div>
      {rows.length === 0 ? (
        <div className="text-xs text-slate-500 py-4 text-center" data-testid="risk-radar-empty">No risk flags. Strong operational health.</div>
      ) : (
        <div className="space-y-1.5" data-testid="risk-radar-list">
          {rows.map((m) => (
            <div key={m.userId} className="p-2.5 rounded-md bg-slate-50 border border-slate-100" data-testid={`risk-row-${m.userId}`}>
              <div className="flex items-center gap-2 flex-wrap">
                <Badge className={`border text-[10px] px-1.5 py-0 ${SEV[m.severity] || SEV.Medium}`}>{m.severity}</Badge>
                <span className="text-sm font-medium text-slate-900 truncate">{m.name}</span>
                {m.estCostGBP > 0 && <span className="text-[11px] font-mono text-rose-700">{fmtGBP(m.estCostGBP)}</span>}
              </div>
              <div className="text-[11px] text-slate-600 mt-0.5">{m.issue}</div>
              <div className="text-[11px] text-slate-500 mt-0.5 italic">{m.recommendedAction}</div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}
